// Transfer page: atomic stock transfer between two warehouses.
import { useMemo, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { Trash2, Plus, ScanLine } from "lucide-react";
import { toast } from "@/components/ui/toast";
import { useScanResolve } from "./use-scan-resolve";

interface Warehouse {
  id: string;
  name: string;
  isActive: boolean;
}

interface Variant {
  id: string;
  sku: string;
  productName: string;
}

interface Line {
  variantId: string;
  sku: string;
  productName: string;
  quantity: number;
  serialIds: string[]; // empty means quantity-only transfer
}

interface Serial {
  id: string;
  serialNo: string;
  variantId: string;
  status: string;
  currentWarehouseId?: string | null;
}

export function TransferPage() {
  const qc = useQueryClient();
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [variantQuery, setVariantQuery] = useState("");
  const [serialInput, setSerialInput] = useState("");
  const [activeLineIdx, setActiveLineIdx] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Serial-first scan box — disabled until source warehouse is selected.
  const [imeiInput, setImeiInput] = useState("");
  const [imeiScanning, setImeiScanning] = useState(false);
  const imeiRef = useRef<HTMLInputElement>(null);

  // Context for the scan resolver: transfer requires the source warehouse ID.
  // When fromId is empty the hook is still instantiated but the scan box is
  // disabled, so resolve() is never called without a valid warehouseId.
  const scanCtx = useMemo(
    () => ({ context: "transfer" as const, warehouseId: fromId }),
    [fromId],
  );
  const { resolve: resolveScan } = useScanResolve(scanCtx);

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      apiClient.get<{ data: Warehouse[] }>("/v1/warehouses").then((r) => r.data.data),
  });
  const activeWarehouses = useMemo(() => warehouses.filter((w) => w.isActive), [warehouses]);

  const { data: variantResults = [] } = useQuery({
    queryKey: ["variant-search", variantQuery],
    queryFn: () =>
      apiClient
        .get<{ data: Variant[] }>("/v1/variants/search", { params: { q: variantQuery } })
        .then((r) => r.data.data),
    enabled: variantQuery.trim().length >= 2,
  });

  const submitMutation = useMutation({
    mutationFn: (payload: object) =>
      apiClient.post<{ data: { transferId: string } }>("/v1/inventory/transfers", payload)
        .then((r) => r.data.data),
    onSuccess: (data) => {
      setFeedback(`Chuyển kho thành công (ID: ${data.transferId.slice(0, 8)}…)`);
      setLines([]);
      setNote("");
      qc.invalidateQueries({ queryKey: ["stock"] });
    },
    onError: (err: { response?: { data?: { error?: { detail?: string } } } }) => {
      setFeedback(err.response?.data?.error?.detail ?? "Có lỗi xảy ra");
    },
  });

  // Variant-first add (existing flow): creates an empty line for the variant.
  function addLine(v: Variant) {
    if (lines.some((l) => l.variantId === v.id)) return;
    setLines((ls) => [
      ...ls,
      { variantId: v.id, sku: v.sku, productName: v.productName, quantity: 1, serialIds: [] },
    ]);
    setVariantQuery("");
  }

  // Per-line serial attachment (existing SKU-first flow, unchanged).
  async function attachSerial(lineIdx: number) {
    const code = serialInput.trim();
    if (!code) return;
    try {
      const sr = await apiClient
        .get<{ data: Serial }>(`/v1/serials/${encodeURIComponent(code)}`)
        .then((r) => r.data.data);
      if (sr.variantId !== lines[lineIdx].variantId) {
        setFeedback(`Serial ${code} không thuộc SKU này`);
        return;
      }
      if (sr.status !== "in_stock" || sr.currentWarehouseId !== fromId) {
        setFeedback(`Serial ${code} không nằm trong kho nguồn`);
        return;
      }
      setLines((ls) =>
        ls.map((l, i) =>
          i === lineIdx && !l.serialIds.includes(sr.id)
            ? { ...l, serialIds: [...l.serialIds, sr.id], quantity: l.serialIds.length + 1 }
            : l,
        ),
      );
      setSerialInput("");
      setFeedback(null);
    } catch {
      setFeedback(`Không tìm thấy serial ${code}`);
    }
  }

  // Serial-first scan handler: quét IMEI → resolve → tự tìm/tạo dòng + gắn serial.
  async function handleImeiKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = imeiInput.trim();
    if (!code || imeiScanning) return;

    setImeiScanning(true);
    try {
      const sr = await resolveScan(code);
      setImeiInput("");

      if (sr.kind === "none") {
        toast.error("Không tìm thấy serial hoặc sản phẩm");
        return;
      }

      if (sr.kind === "variant") {
        // Fuzzy match — single result: add empty line; multiple: surface picker
        if (sr.matches.length === 1) {
          const m = sr.matches[0];
          addLine({ id: m.id, sku: m.sku, productName: m.productName });
        } else {
          setVariantQuery(code);
          toast.info("Nhiều sản phẩm khớp — chọn từ danh sách bên dưới");
        }
        return;
      }

      // kind === "serial"
      if (!sr.ok) {
        toast.error(sr.reason ?? "Serial không hợp lệ cho kho nguồn này");
        return;
      }

      const variantId = sr.variant.id;
      const serialId = sr.serial.id;

      setLines((ls) => {
        const existing = ls.find((l) => l.variantId === variantId);

        if (existing) {
          // Dedup guard: serial already attached to this line
          if (existing.serialIds.includes(serialId)) {
            toast.info("Serial đã có trong phiếu chuyển kho");
            return ls;
          }
          return ls.map((l) =>
            l.variantId === variantId
              ? {
                  ...l,
                  serialIds: [...l.serialIds, serialId],
                  // qty tracks number of serials attached (invariant: qty >= len(serialIds))
                  quantity: l.serialIds.length + 1,
                }
              : l,
          );
        }

        // No line for this variant yet — create one with the serial already attached
        return [
          ...ls,
          {
            variantId,
            sku: sr.variant.sku,
            productName: sr.variant.productName,
            quantity: 1,
            serialIds: [serialId],
          },
        ];
      });
    } finally {
      setImeiScanning(false);
      setTimeout(() => imeiRef.current?.focus(), 60);
    }
  }

  function canSubmit() {
    return (
      fromId &&
      toId &&
      fromId !== toId &&
      lines.length > 0 &&
      lines.every((l) => l.quantity > 0)
    );
  }

  function submit() {
    submitMutation.mutate({
      fromWarehouseId: fromId,
      toWarehouseId: toId,
      note,
      items: lines.map((l) => ({
        variantId: l.variantId,
        quantity: l.quantity,
        serialIds: l.serialIds,
      })),
    });
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-2xl font-semibold">Chuyển kho</h1>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Kho nguồn</Label>
          <select
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            value={fromId}
            onChange={(e) => setFromId(e.target.value)}
          >
            <option value="">— Chọn —</option>
            {activeWarehouses.map((w) => (
              <option key={w.id} value={w.id} disabled={w.id === toId}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Kho đích</Label>
          <select
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            value={toId}
            onChange={(e) => setToId(e.target.value)}
          >
            <option value="">— Chọn —</option>
            {activeWarehouses.map((w) => (
              <option key={w.id} value={w.id} disabled={w.id === fromId}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Ghi chú</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Lý do/Chứng từ…" />
      </div>

      {/* Serial-first IMEI scan box — gated on source warehouse selection */}
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5">
          <ScanLine size={14} />
          Quét IMEI / Serial
        </Label>
        <div className="relative">
          <Input
            ref={imeiRef}
            value={imeiInput}
            onChange={(e) => setImeiInput(e.target.value)}
            onKeyDown={handleImeiKey}
            placeholder={
              fromId
                ? "Quét IMEI hoặc số serial, Enter để thêm vào phiếu…"
                : "Chọn kho nguồn trước khi quét serial"
            }
            disabled={!fromId || imeiScanning}
            className="font-mono text-sm"
            autoComplete="off"
          />
          {imeiScanning && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              Đang tra cứu…
            </span>
          )}
        </div>
        {!fromId && (
          <p className="text-xs text-amber-600">
            Chọn kho nguồn để bật quét IMEI/serial
          </p>
        )}
      </div>

      {/* Variant picker (SKU-first flow — unchanged) */}
      <div className="space-y-1.5">
        <Label>Thêm sản phẩm theo SKU</Label>
        <Input
          value={variantQuery}
          onChange={(e) => setVariantQuery(e.target.value)}
          placeholder="Tìm SKU hoặc tên sản phẩm…"
        />
        {variantQuery.trim().length >= 2 && variantResults.length > 0 && (
          <div className="rounded-md border bg-card max-h-48 overflow-y-auto">
            {variantResults.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => addLine(v)}
                className="block w-full text-left px-3 py-2 hover:bg-muted text-sm"
              >
                <span className="font-mono text-xs mr-2">{v.sku}</span>
                {v.productName}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lines */}
      {lines.length > 0 && (
        <Table>
          <THead>
            <Tr>
              <Th>SKU</Th>
              <Th>Tên sản phẩm</Th>
              <Th className="w-28 text-right">Số lượng</Th>
              <Th>Serial</Th>
              <Th className="w-12" />
            </Tr>
          </THead>
          <TBody>
            {lines.map((l, idx) => (
              <Tr key={l.variantId}>
                <Td className="font-mono text-xs">{l.sku}</Td>
                <Td>{l.productName}</Td>
                <Td className="text-right">
                  <Input
                    type="number"
                    min={1}
                    value={l.quantity}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, i) => (i === idx ? { ...x, quantity: parseInt(e.target.value) || 0 } : x)),
                      )
                    }
                    disabled={l.serialIds.length > 0}
                    className="h-8 text-right"
                  />
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {l.serialIds.map((sid) => (
                      <span key={sid} className="rounded bg-muted px-2 py-0.5 text-xs font-mono">
                        {sid.slice(0, 8)}…
                      </span>
                    ))}
                  </div>
                  {activeLineIdx === idx ? (
                    <div className="flex gap-1">
                      <Input
                        value={serialInput}
                        onChange={(e) => setSerialInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void attachSerial(idx);
                          }
                        }}
                        placeholder="Quét/nhập serial…"
                        className="h-8"
                        autoFocus
                      />
                      <Button size="sm" onClick={() => setActiveLineIdx(null)} variant="ghost">
                        Đóng
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setActiveLineIdx(idx)} disabled={!fromId}>
                      <Plus size={12} className="mr-1" /> Serial
                    </Button>
                  )}
                </Td>
                <Td>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {feedback && (
        <p className={submitMutation.isError ? "text-destructive" : "text-green-700"}>{feedback}</p>
      )}

      <div className="flex gap-2">
        <Button onClick={submit} disabled={!canSubmit() || submitMutation.isPending}>
          {submitMutation.isPending ? "Đang xử lý…" : "Xác nhận chuyển kho"}
        </Button>
      </div>
    </div>
  );
}
